import React from 'react';
import { BodyText, FlexRow } from '@make-software/cspr-design';
import { Deploy, NftTokenEntryPoint } from '@block-explorer/entities';
import DeployActionDefault from './DeployActionDefault';
import { nftTokenMessage } from '../../../services/deploy-action-messages';
import { guardedDeriveSplitDataFromArguments } from '../../../services/deploy-action-messages/utils.ts';
import { HashFontSize, ContractIdentifier } from '../../../components';
import { useTranslation } from 'react-i18next';
import { NftCollectionIdentifier } from './NftCollectionIdentifier';
import { NftActionRow, UpdateMetadataNFTAction } from './NftActionRow';
import { useDeployActionDataContext } from '../services/deploy-action-context';

const SetAllApprovalsNFTAction = ({ deploy }: { deploy: Deploy }) => {
  const { contractPackage, args } = deploy;
  const { getContractPackagePath } = useDeployActionDataContext();
  const operatorHash =
    args.operator && guardedDeriveSplitDataFromArguments(args.operator, 'Hash');

  return (
    <FlexRow align={'center'} itemsSpacing={8}>
      <BodyText monotype size={3} wordBreak noWrap variation={'black'}>
        Approve transfer rights
      </BodyText>
      <BodyText size={3} noWrap variation={'darkGray'}>
        of
      </BodyText>
      <BodyText monotype size={3} wordBreak noWrap variation={'black'}>
        all
      </BodyText>
      <NftCollectionIdentifier
        collectionHash={contractPackage.contract_package_hash}
        getContractPackagePath={getContractPackagePath}
      />

      {operatorHash && (
        <>
          <BodyText size={3} noWrap variation={'darkGray'}>
            to
          </BodyText>
          <ContractIdentifier
            hash={operatorHash.hash}
            avatarSize={'small'}
            hashFontSize={HashFontSize.big}
          />
        </>
      )}
    </FlexRow>
  );
};

interface DeployNftActionProps {
  deploy: Deploy;
}

export const DeployActionNft = ({ deploy }: DeployNftActionProps) => {
  const { t } = useTranslation();
  const { getPublicKeyAccountHash } = useDeployActionDataContext();
  const message = nftTokenMessage().prepareActionMessageDataForDeployDetails(
    deploy,
    getPublicKeyAccountHash,
    t
  );

  if (!message) {
    return <DeployActionDefault deploy={deploy} />;
  }

  const {
    entryPointName,
    actionName,
    nftTokenIds,
    contractPrefix,
    account1,
    account2,
    prefix1,
  } = message;

  if (entryPointName === NftTokenEntryPoint.set_approval_for_all) {
    return <SetAllApprovalsNFTAction deploy={deploy} />;
  }

  if (entryPointName === NftTokenEntryPoint.update_token_meta) {
    return (
      <UpdateMetadataNFTAction
        actionName={actionName}
        contractPackage={deploy.contractPackage}
        nftTokenIds={nftTokenIds}
      />
    );
  }

  return (
    <NftActionRow
      actionName={actionName}
      senderPrefix={prefix1}
      contractPrefix={contractPrefix}
      nftTokenIds={nftTokenIds}
      contract_package={deploy.contractPackage}
      from_public_key={account1?.publicKey}
      from_hash={account1?.hash}
      from_type={account1?.hashType}
      to_type={account2?.hashType}
      to_public_key={account2?.publicKey}
      to_hash={account2?.hash}
    />
  );
};
